import { useMemo, useState } from 'react'
import Select, { SingleValue } from 'react-select'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'

type Option = { value: string; label: string }

const SimulationPage = () => {
  const [selectedCompany, setSelectedCompany] = useState<Option | null>(null)
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [withEvents, setWithEvents] = useState<boolean>(false)
  const [eventsFile, setEventsFile] = useState<File | null>(null)
  const [isRunning, setIsRunning] = useState<boolean>(false)
  const [showResults, setShowResults] = useState<boolean>(false)

  const companyOptions = useMemo<Option[]>(
    () => [
      { value: 'acme', label: 'ACME Energy' },
      { value: 'globex', label: 'Globex' },
      { value: 'initech', label: 'Initech' },
    ],
    []
  )

  const kpis = useMemo(
    () => [
      { title: 'Predicted Total', value: '4,820 kWh', change: '+3.2% vs baseline' },
      { title: 'Peak Demand', value: '312 kW', change: 'at 13:00' },
      { title: 'Baseline Load', value: '58 kW', change: 'night hours' },
    ],
    []
  )

  const predictionOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'line', toolbar: { show: false } },
      stroke: { curve: 'smooth', width: 3 },
      dataLabels: { enabled: false },
      xaxis: { categories: ['D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7'] },
      yaxis: { labels: { formatter: (v) => `${v} kWh` } },
      colors: ['#3E97FF', '#50CD89'],
      legend: { position: 'top' },
    }),
    []
  )
  const predictionSeries = useMemo(
    () => [
      { name: 'Predicted', data: [650, 690, 710, 720, 680, 640, 730] },
      { name: 'Historical', data: [600, 620, 640, 680, 660, 610, 700] },
    ],
    []
  )

  const comparisonOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'bar', stacked: false, toolbar: { show: false } },
      plotOptions: { bar: { columnWidth: '40%' } },
      dataLabels: { enabled: false },
      xaxis: { categories: ['Week Avg'] },
      yaxis: { labels: { formatter: (v) => `${v} kWh` } },
      colors: ['#50CD89', '#F1416C'],
      legend: { position: 'bottom' },
    }),
    []
  )
  const comparisonSeries = useMemo(
    () => [
      { name: 'With Events', data: [690] },
      { name: 'Without Events', data: [660] },
    ],
    []
  )

  const handleDownloadEventsTemplate = () => {
    const csvContent = [
      'event_name,event_date,event_weight',
      'Campaign,2025-03-01,0.9',
      'Holiday,2025-03-21,1.1',
    ].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'simulation_events_template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const runSimulation = () => {
    setShowResults(false)
    setIsRunning(true)
    setTimeout(() => {
      setIsRunning(false)
      setShowResults(true)
    }, 1500)
  }

  return (
    <div className='container-fluid'>
      <div className='card mb-10'>
        <div className='card-body p-10'>
          <div className='row g-5 align-items-end'>
            <div className='col-12 col-md-4'>
              <label className='form-label'>Company</label>
              <Select<Option, false>
                classNamePrefix='react-select'
                placeholder='Select company'
                value={selectedCompany}
                onChange={(opt: SingleValue<Option>) => setSelectedCompany(opt)}
                options={companyOptions}
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label'>Start date</label>
              <input
                type='date'
                className='form-control'
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label'>End date</label>
              <input
                type='date'
                className='form-control'
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className='col-12 col-md-2'>
              <label className='form-label d-block'>Include events?</label>
              <div className='form-check form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='withEventsSwitch'
                  checked={withEvents}
                  onChange={(e) => setWithEvents(e.target.checked)}
                />
                <label className='form-check-label' htmlFor='withEventsSwitch'>
                  {withEvents ? 'Yes' : 'No'}
                </label>
              </div>
            </div>
          </div>

          {withEvents && (
            <div className='row g-5 mt-1'>
              <div className='col-12 col-md-6'>
                <label className='form-label'>Events (CSV import)</label>
                <input
                  type='file'
                  accept='.csv'
                  className='form-control'
                  onChange={(e) => setEventsFile(e.target.files?.[0] ?? null)}
                />
                {eventsFile ? (
                  <div className='form-text'>Uploaded: {eventsFile.name}</div>
                ) : (
                  <div className='form-text'>event_name,event_date,event_weight</div>
                )}
              </div>
              <div className='col-12 col-md-3 d-flex align-items-end'>
                <button className='btn btn-light-primary' onClick={handleDownloadEventsTemplate}>
                  Download sample CSV
                </button>
              </div>
            </div>
          )}

          <div className='mt-7 d-flex gap-3'>
            <button
              className='btn btn-primary'
              onClick={runSimulation}
              disabled={!selectedCompany || !startDate || !endDate || isRunning}
            >
              Create Simulation
            </button>
            {isRunning && (
              <div className='d-flex align-items-center gap-2'>
                <span
                  className='spinner-border spinner-border-sm'
                  role='status'
                  aria-hidden='true'
                />
                <span>Processing...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {showResults && (
        <>
          <div className='row g-5 mb-5'>
            {kpis.map((kpi) => (
              <div className='col-12 col-md-4' key={kpi.title}>
                <div className='card h-100'>
                  <div className='card-body d-flex flex-column justify-content-center'>
                    <div className='fw-bold text-gray-600'>{kpi.title}</div>
                    <div className='fs-2 fw-bolder mt-2'>{kpi.value}</div>
                    <div className='text-muted'>{kpi.change}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className='row g-5'>
            <div className='col-12 col-xl-6'>
              <div className='card h-100'>
                <div className='card-header border-0 pt-5'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bold fs-4'>Consumption Forecast</span>
                    <span className='text-muted mt-1 fw-semibold'>Forecast for selected range</span>
                  </h3>
                </div>
                <div className='card-body'>
                  <ReactApexChart
                    options={predictionOptions}
                    series={predictionSeries}
                    type='line'
                    height={320}
                  />
                </div>
              </div>
            </div>
            <div className='col-12 col-xl-6'>
              <div className='card h-100'>
                <div className='card-header border-0 pt-5'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bold fs-4'>Infographics</span>
                    <span className='text-muted mt-1 fw-semibold'>Peak hour, base load, etc.</span>
                  </h3>
                </div>
                <div className='card-body'>
                  <div className='row g-5'>
                    <div className='col-6'>
                      <div className='bg-light rounded p-5 text-center'>
                        <div className='fw-bold fs-2'>+6.3%</div>
                        <div className='text-muted'>YoY Trend</div>
                      </div>
                    </div>
                    <div className='col-6'>
                      <div className='bg-light rounded p-5 text-center'>
                        <div className='fw-bold fs-2'>12:30</div>
                        <div className='text-muted'>Peak Hour</div>
                      </div>
                    </div>
                    <div className='col-12'>
                      <div
                        className='bg-light rounded d-flex align-items-center justify-content-center'
                        style={{ height: 120 }}
                      >
                        <span className='text-muted'>Sankey/Flow placeholder</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='col-12'>
              <div className='card'>
                <div className='card-header border-0 pt-5'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bold fs-4'>Comparative Results</span>
                    <span className='text-muted mt-1 fw-semibold'>With Events vs Without</span>
                  </h3>
                </div>
                <div className='card-body'>
                  <ReactApexChart
                    options={comparisonOptions}
                    series={comparisonSeries}
                    type='bar'
                    height={280}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default SimulationPage
